import {nextTick, ref} from "vue";
import {useRoute} from "vue-router";

/** Options for {@link useTableRowFocus}. All are optional; missing options simply disable the highlight. */
export interface TableRowFocusOptions<T> {
	/** Returns the currently displayed rows in table order. */
	getRows?: () => T[];
	/** Reads the unique id of a row; it is compared against the route query value. */
	rowKey?: (row: T) => string;
	/** Route query parameter holding the id of the row to focus. Default: "focusKey". */
	queryParam?: string;
	/** Initial page size, used to find the paginator page holding the row. Default: 25. */
	pageSize?: number;
	/** Duration of the blink highlight in milliseconds. Default: 2400. */
	blinkDurationMs?: number;
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Focuses Table row referenced by a route query parameter
 * it jumps to the row's paginator page, scrolls it into
 * view and highlights it
 *
 * Bind to the DataTable: `ref="tableWrapper"` on a wrapping element, `:first="firstRow"`,
 * `:rows="rowsPerPage"`, `:rowClass="rowClass"`, `@page="onPage"`, then call
 * `focusRequestedRow()` once the rows are loaded
 */
export function useTableRowFocus<T>(options: TableRowFocusOptions<T> = {}) {
	const {getRows, rowKey, queryParam = "focusKey", blinkDurationMs = 2400} = options;
	const initialPageSize = options.pageSize && options.pageSize > 0 ? options.pageSize : DEFAULT_PAGE_SIZE;
	const route = useRoute();

	/** Wrapping element used to locate the highlighted row in the DOM. */
	const tableWrapper = ref<HTMLElement | null>(null);
	/** Id of the row to highlight; null when nothing is focused. */
	const focusedKey = ref<string | null>(null);
	/** Whether the focused row is currently playing the blink animation. */
	const blinkRow = ref(false);
	/** Guards against re-focusing after saves/reloads (focus happens once per navigation). */
	const hasFocused = ref(false);
	/** Paginator offset and page size, kept in sync via {@link onPage}. */
	const firstRow = ref(0);
	const rowsPerPage = ref(initialPageSize);

	/**
	 * Reads a row's id
	 *  returns null if no key found
	 *
	 */
	const readRowKey = (row: T): string | null => {
		if (typeof rowKey !== "function" || row == null) {
			return null;
		}
		try {
			const key = rowKey(row);
			return typeof key === "string" ? key : null;
		} catch {
			return null;
		}
	};

	/** CSS classes marking the focused row for the highlight and blink. */
	const rowClass = (data: T): string => {
		if (!focusedKey.value || readRowKey(data) !== focusedKey.value) {
			return "";
		}
		return blinkRow.value ? "table-row--focus table-row--blink" : "table-row--focus";
	};

	/** Keeps the paginator offset and page size in sync with the DataTable. */
	const onPage = (event?: {first?: number; rows?: number}): void => {
		if (typeof event?.first === "number") {
			firstRow.value = event.first;
		}
		if (typeof event?.rows === "number" && event.rows > 0) {
			rowsPerPage.value = event.rows;
		}
	};

	/** Scrolls the row named by the query parameter into view and highlights it. */
	const focusRequestedRow = async (): Promise<void> => {
		if (hasFocused.value || typeof getRows !== "function") {
			return;
		}

		const queryValue = route.query[queryParam];
		const focusKey = Array.isArray(queryValue) ? queryValue[0] : queryValue;
		if (!focusKey) {
			return;
		}

		const rows = getRows();
		const index = Array.isArray(rows) ? rows.findIndex((row: T) => readRowKey(row) === focusKey) : -1;
		if (index === -1) {
			return;
		}

		hasFocused.value = true;
		focusedKey.value = focusKey;
		const pageSize = rowsPerPage.value > 0 ? rowsPerPage.value : DEFAULT_PAGE_SIZE;
		firstRow.value = Math.floor(index / pageSize) * pageSize;

		await nextTick();

		const rowElement = tableWrapper.value?.querySelector(".table-row--focus");
		rowElement?.scrollIntoView({block: "center", behavior: "smooth"});

		blinkRow.value = true;
		window.setTimeout(() => {
			blinkRow.value = false;
		}, blinkDurationMs);
	};

	return {tableWrapper, firstRow, rowsPerPage, rowClass, onPage, focusRequestedRow};
}
